import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { useToast } from "../../../../components/toast";
import { CompetitionResults } from "../../../../types/competition.types";
import { ConfirmedTeamDataFResults } from "../../../../types/team.types";
import { TeamsAPI } from "../../../../APIs/teams.api";
import { CategoriesAPI } from "../../../../APIs/categories.api";
import { FishSpeciesAPI } from "../../../../APIs/fish_species.api";
import { CompetitionsAPI } from "../../../../APIs/competitions.api";
import CrudTable from "../../../../components/crud-table";
import { CreateOverallResultModal } from "./modals/create-overall-results";
import { CreateBiggestFishModal } from "./modals/create-biggest-fish";
import { CreateCategoryResultModal } from "./modals/create-category-result";
import { Loading } from "../../../../components/loading";
import { Button } from "../../../../components/button";
import { CrudTableHeader } from "../../../../components/crud-table-header";

interface Category {
  id: number;
  name: string;
}

interface FishSpecies {
  id: number;
  name: string;
}

export function CompetitionResultsAdmin() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [results, setResults] = useState<CompetitionResults>({
    overall_results: [],
    category_results: [],
    biggest_fish_results: [],
  });
  const [teams, setTeams] = useState<ConfirmedTeamDataFResults[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [fishSpecies, setFishSpecies] = useState<FishSpecies[]>([]);

  const [loading, setLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(false);

  const [createOverallModal, setCreateOverallModal] = useState(false);
  const [createBiggestFishModal, setCreateBiggestFishModal] = useState(false);
  const [createCategoryModal, setCreateCategoryModal] = useState(false);

  const competitionId = parseInt(id!);

  const getAvailablePositions = () => {
    const usedPositions = (results.overall_results || []).map(
      (r) => r.final_position
    );
    const allPositions = Array.from({ length: 15 }, (_, i) => i + 1);
    return allPositions.filter((pos) => !usedPositions.includes(pos));
  };

  const getAvailableTeamsForOverall = () => {
    const usedTeamIds = (results.overall_results || []).map((r) => r.team.id);
    return teams.filter((team) => !usedTeamIds.includes(team.id));
  };

  const getAvailableFishSpecies = () => {
    const usedSpeciesIds = (results.biggest_fish_results || []).map(
      (r) => r.species.id
    );
    return fishSpecies.filter(
      (species) => !usedSpeciesIds.includes(species.id)
    );
  };

  const getAvailableCategories = () => {
    const usedCategoryIds = (results.category_results || []).map(
      (r) => r.category.id
    );
    return categories.filter(
      (category) => !usedCategoryIds.includes(category.id)
    );
  };

  const canAddOverallResult =
    getAvailablePositions().length > 0 &&
    getAvailableTeamsForOverall().length > 0;
  const canAddBiggestFishResult =
    getAvailableFishSpecies().length > 0 && teams.length > 0;
  const canAddCategoryResult =
    getAvailableCategories().length > 0 && teams.length > 0;
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [teamsRes, categoriesRes, fishSpeciesRes] = await Promise.all([
        TeamsAPI.getAcceptedTeams(competitionId),
        CategoriesAPI.getCategories(),
        FishSpeciesAPI.getFishSpecies(),
      ]);
      if (teamsRes.statusCode === 200) {
        setTeams(Array.isArray(teamsRes.data) ? teamsRes.data : []);
      }
      if (categoriesRes.statusCode === 200) {
        setCategories(
          Array.isArray(categoriesRes.data) ? categoriesRes.data : []
        );
      }
      if (fishSpeciesRes.statusCode === 200) {
        setFishSpecies(
          Array.isArray(fishSpeciesRes.data) ? fishSpeciesRes.data : []
        );
      }
    } catch (error) {
      addToast({
        type: "error",
        message: "Error al cargar los datos iniciales",
        location: "bottom-center",
      });
    } finally {
      setLoading(false);
    }
  }, [competitionId, addToast]);

  const loadResults = useCallback(async () => {
    try {
      setResultsLoading(true);
      const response = await CompetitionsAPI.getCompetitionResults(
        competitionId
      );
      if (response.statusCode === 200) {
        const data = response.data;
        if (Array.isArray(data) && data.length === 0) {
          setResults({
            overall_results: [],
            category_results: [],
            biggest_fish_results: [],
          });
        } else {
          setResults(
            data || {
              overall_results: [],
              category_results: [],
              biggest_fish_results: [],
            }
          );
        }
      }
    } catch (error) {
      addToast({
        type: "error",
        message: "Error al cargar los resultados",
        location: "bottom-center",
      });
    } finally {
      setResultsLoading(false);
    }
  }, [competitionId, addToast]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const handleCreateSuccess = () => {
    loadResults();
    setCreateOverallModal(false);
    setCreateBiggestFishModal(false);
    setCreateCategoryModal(false);
    addToast({
      type: "success",
      message: "Resultado creado correctamente",
      location: "bottom-center",
    });
  };

  const handleDeleteOverall = async (result: any) => {
    try {
      const response = await CompetitionsAPI.deleteCompetitionResult(result.id);
      if (response && response.statusCode === 200) {
        addToast({
          type: "success",
          message: "Resultado eliminado correctamente",
          location: "bottom-center",
        });
        loadResults();
      } else {
        throw new Error("Error al eliminar el resultado");
      }
    } catch (error) {
      addToast({
        type: "error",
        message: "Error al eliminar el resultado",
        location: "bottom-center",
      });
    }
  };

  const handleDeleteBiggestFish = async (result: any) => {
    try {
      const response = await CompetitionsAPI.deleteBiggestFishResult(result.id);
      if (response && response.statusCode === 200) {
        addToast({
          type: "success",
          message: "Resultado eliminado correctamente",
          location: "bottom-center",
        });
        loadResults();
      } else {
        throw new Error("Error al eliminar el resultado");
      }
    } catch (error) {
      console.error("Error deleting result:", error);
      addToast({
        type: "error",
        message: "Error al eliminar el resultado",
        location: "bottom-center",
      });
    }
  };

  const handleDeleteCategory = async (result: any) => {
    try {
      const response = await CompetitionsAPI.deleteTopCategoryResult(result.id);
      if (response && response.statusCode === 200) {
        addToast({
          type: "success",
          message: "Resultado eliminado correctamente",
          location: "bottom-center",
        });
        loadResults();
      } else {
        throw new Error("Error al eliminar el resultado");
      }
    } catch (error) {
      addToast({
        type: "error",
        message: "Error al eliminar el resultado de categoría",
        location: "bottom-center",
      });
    }
  };

  const overallResultsColumns = [
    {
      key: "final_position",
      label: "Posición",
      render: (value: number) => `${value}°`,
      sortable: true,
    },
    {
      key: "team.name",
      label: "Equipo",
    },
    {
      key: "team.institution_name",
      label: "Barra",
      render: (value: string) => value || "-",
    },
    {
      key: "team.members",
      label: "Participantes",
      render: (value: any, row: any) => {
        if (!row.team.members || row.team.members.length === 0) return "-";

        return (
          <div>
            {row.team.members.map((member: any, index: number) => (
              <div key={member.id} className="text-xs font-medium">
                {member.first_name} {member.last_name}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "points",
      label: "Puntos",
      sortable: true,
    },
    {
      key: "species_count",
      label: "Especies",
      sortable: true,
    },
    {
      key: "pieces_count",
      label: "Piezas",
      sortable: true,
    },
  ];

  const biggestFishColumns = [
    {
      key: "species.name",
      label: "Especie",
      sortable: true,
    },
    {
      key: "team.name",
      label: "Equipo",
      sortable: true,
    },
    {
      key: "team.institution_name",
      label: "Barra",
      render: (value: string) => value || "-",
    },
    {
      key: "participant.first_name",
      label: "Participante",
      sortable: true,
      render: (value: string, row: any) => {
        return `${row.participant.first_name} ${row.participant.last_name}`;
      },
    },

    {
      key: "size_cm",
      label: "Tamaño (cm)",
      render: (value: number) => `${value} cm`,
      sortable: true,
    },
    {
      key: "points",
      label: "Puntos",
      sortable: true,
    },
  ];

  const categoryResultsColumns = [
    {
      key: "category.name",
      label: "Categoría",
      sortable: true,
    },
    {
      key: "team.name",
      label: "Equipo",
      sortable: true,
    },
    {
      key: "team.institution_name",
      label: "Barra P.",
      render: (value: string) => value || "-",
    },
    {
      key: "team.members",
      label: "Participantes",
      render: (value: any, row: any) => {
        if (!row.team.members || row.team.members.length === 0) return "-";

        return (
          <div>
            {row.team.members.map((member: any, index: number) => (
              <div key={member.id} className="text-xs font-medium">
                {member.first_name} {member.last_name}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "points",
      label: "Puntos",
      sortable: true,
    },
    {
      key: "species_count",
      label: "Especies",
      sortable: true,
    },
    {
      key: "pieces_count",
      label: "Piezas",
      sortable: true,
    },
  ];

  if (loading) {
    return <Loading />;
  }

  const sortedOverallResults = (results.overall_results || []).sort(
    (a, b) => a.final_position - b.final_position
  );

  return (
    <div>
      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Administrar Resultados
              </h1>
              <p className="text-gray-600">
                Gestionar los resultados de la competencia
              </p>
            </div>
          </div>
          <Button
            type="button"
            text="Volver"
            onClick={() => navigate("/dashboard/competitions")}
            variant="back"
          />
        </div>
      </div>

      {/* Overall Results Table */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <CrudTableHeader
          title={`Resultados (${(results.overall_results || []).length}/15)`}
        />
        {resultsLoading ? (
          <Loading />
        ) : (
          <CrudTable
            data={sortedOverallResults}
            columns={overallResultsColumns}
            onAdd={
              canAddOverallResult
                ? {
                    onClick: () => setCreateOverallModal(true),
                    label: "Agregar Resultado",
                  }
                : undefined
            }
            actions={{
              delete: {
                label: "",
                icon: <Trash2 className="h-4 w-4" />,
                onClick: handleDeleteOverall,
              },
            }}
            deleteConfirmation={{
              titleField: "team.name",
              titlePrefix:
                "¿Estás seguro de que deseas eliminar el resultado del equipo",
              confirmLabel: "Eliminar",
              cancelLabel: "Cancelar",
            }}
            mobileCardTitle={`final_position`}
          />
        )}
      </div>

      {/* Biggest Fish Results Table */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <CrudTableHeader
          title={`Pieza Mayor (${(results.biggest_fish_results || []).length}/${
            fishSpecies.length
          })`}
        />

        {resultsLoading ? (
          <Loading />
        ) : (
          <CrudTable
            data={results.biggest_fish_results || []}
            columns={biggestFishColumns}
            onAdd={
              canAddBiggestFishResult
                ? {
                    onClick: () => setCreateBiggestFishModal(true),
                    label: "Agregar Pieza Mayor",
                  }
                : undefined
            }
            actions={{
              delete: {
                label: "",
                icon: <Trash2 className="h-4 w-4" />,
                onClick: handleDeleteBiggestFish,
              },
            }}
            deleteConfirmation={{
              titleField: "species.name",
              titlePrefix:
                "¿Estás seguro de que deseas eliminar el resultado de pieza mayor de",
              confirmLabel: "Eliminar",
              cancelLabel: "Cancelar",
            }}
            mobileCardTitle="species.name"
          />
        )}
      </div>

      {/* Category Results Table */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <CrudTableHeader
          title={`Grupos (${(results.category_results || []).length}/${
            categories.length
          })`}
        />

        {resultsLoading ? (
          <div className="text-center py-8">Cargando resultados...</div>
        ) : (
          <CrudTable
            data={results.category_results || []}
            columns={categoryResultsColumns}
            onAdd={
              canAddCategoryResult
                ? {
                    onClick: () => setCreateCategoryModal(true),
                    label: "Agregar Resultado de Categoría",
                  }
                : undefined
            }
            actions={{
              delete: {
                label: "",
                icon: <Trash2 className="h-4 w-4" />,
                onClick: handleDeleteCategory,
              },
            }}
            deleteConfirmation={{
              titleField: "category.name",
              titlePrefix:
                "¿Estás seguro de que deseas eliminar el resultado de la categoría",
              confirmLabel: "Eliminar",
              cancelLabel: "Cancelar",
            }}
            mobileCardTitle="category.name"
          />
        )}
      </div>

      {/* Modals */}
      <CreateOverallResultModal
        isOpen={createOverallModal}
        onClose={() => setCreateOverallModal(false)}
        onSuccess={handleCreateSuccess}
        competitionId={competitionId}
        teams={getAvailableTeamsForOverall()}
        availablePositions={getAvailablePositions()}
      />

      <CreateBiggestFishModal
        isOpen={createBiggestFishModal}
        onClose={() => setCreateBiggestFishModal(false)}
        onSuccess={handleCreateSuccess}
        competitionId={competitionId}
        teams={teams}
        fishSpecies={getAvailableFishSpecies()}
      />

      <CreateCategoryResultModal
        isOpen={createCategoryModal}
        onClose={() => setCreateCategoryModal(false)}
        onSuccess={handleCreateSuccess}
        competitionId={competitionId}
        teams={teams}
        categories={getAvailableCategories()}
      />
    </div>
  );
}
